import {
  GraphBatchClaim,
  GraphBatchExecutionOutcome,
  GraphBatchGateway,
} from './contracts';

export type BatchExecutor = {
  run: (isCancelled: () => boolean) => Promise<GraphBatchExecutionOutcome>;
};

type GraphBatchRunnerArgs = {
  gateway: GraphBatchGateway;
  createExecutor: (claim: GraphBatchClaim) => BatchExecutor;
};

/**
 * Long-lived actor that drains graph batches independently from React render cycles.
 */
export class GraphBatchRunner {
  private readonly gateway: GraphBatchGateway;
  private readonly createExecutor: (claim: GraphBatchClaim) => BatchExecutor;

  private running = false;
  private draining = false;
  private wakeRequested = false;
  private unsubscribe: (() => void) | null = null;

  constructor({ gateway, createExecutor }: GraphBatchRunnerArgs) {
    this.gateway = gateway;
    this.createExecutor = createExecutor;
  }

  start() {
    if (this.running) return;
    this.running = true;
    this.gateway.resetStateMachine();
    this.unsubscribe = this.gateway.subscribeToSignals(() => {
      this.wake();
    });
    this.wake();
  }

  stop() {
    if (!this.running) return;
    this.running = false;
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  wake() {
    if (!this.running) return;
    if (this.draining) {
      this.wakeRequested = true;
      return;
    }
    void this.drain();
  }

  private async executeClaim(
    claim: GraphBatchClaim,
  ): Promise<GraphBatchExecutionOutcome> {
    try {
      const executor = this.createExecutor(claim);
      return await executor.run(() => !this.running);
    } catch (error) {
      console.error('[GraphBatch] Batch execution failed', {
        graphId: claim.batch.graphId,
        entryId: claim.batch.entryId,
        error,
      });
      return { kind: 'failed', error };
    }
  }

  private finishClaim(
    claim: GraphBatchClaim,
    completion: GraphBatchExecutionOutcome,
  ) {
    try {
      this.gateway.finishClaim(claim, completion);
    } catch (error) {
      console.error('[GraphBatch] Failed to finish batch claim', {
        graphId: claim.batch.graphId,
        entryId: claim.batch.entryId,
        error,
      });
    }
  }

  private finishDrain() {
    this.draining = false;
    if (
      this.running &&
      (this.wakeRequested || this.gateway.hasClaimableWork())
    ) {
      this.wake();
    }
  }

  private async drain() {
    if (!this.running) return;
    if (this.draining) return;

    this.draining = true;
    this.wakeRequested = false;

    try {
      while (this.running) {
        const claim = this.gateway.claimNextBatch();
        if (!claim) break;

        const completion = await this.executeClaim(claim);
        this.finishClaim(claim, completion);
      }
    } finally {
      this.finishDrain();
    }
  }
}
